import { useRef } from "react";

import { queryClient, useEntities } from "@/api/core";
import { boostSchema, type Boost } from "@/schemas";

import { BoostQueryKeys } from "../query-keys";
import { CANCEL_BOOST_MUTATION_KEY } from "./useCancelBoost";

type UseBoostsOptions = {
  enabled?: boolean;
  /**
   * Polls while any pending/active boost is present, on a jittered 145–161s
   * interval picked once at mount.
   */
  pollWhileLive?: boolean;
};

const POLL_MIN_MS = 145000;
const POLL_MAX_MS = 161000;

const LIVE_BOOST_STATES: ReadonlySet<Boost["state"]> = new Set([
  "pending_media",
  "pending_moderation",
  "pending_payment",
  "active",
]);

function useBoosts(options: UseBoostsOptions = {}) {
  const { enabled, pollWhileLive = false } = options;
  const jitterRef = useRef<number>(
    Math.floor(Math.random() * (POLL_MAX_MS - POLL_MIN_MS + 1)) + POLL_MIN_MS,
  );
  const hasLiveRef = useRef(false);

  return useEntities<Boost>({
    queryKey: BoostQueryKeys.list(),
    endpoint: "/api/v1/boosts",
    schema: boostSchema,
    enabled,
    refetchInterval: pollWhileLive
      ? () => {
          // A poll firing mid-cancel-mutation can clobber the optimistic
          // "canceled" state with tick-stale server data — suppress.
          if (
            queryClient.isMutating({ mutationKey: CANCEL_BOOST_MUTATION_KEY }) >
            0
          ) {
            return false;
          }
          if (
            typeof document !== "undefined" &&
            document.visibilityState === "hidden"
          ) {
            return false;
          }
          return hasLiveRef.current ? jitterRef.current : false;
        }
      : undefined,
    onSuccess(data: Boost[]) {
      if (pollWhileLive) {
        hasLiveRef.current = data.some((boost) =>
          LIVE_BOOST_STATES.has(boost.state),
        );
      }
    },
  });
}

export { useBoosts, LIVE_BOOST_STATES };
